import { useLazyQuery } from '@apollo/client/react'
import { useEffect, useMemo } from 'react'
import { useUpdateEffect } from 'react-use'

import { gql } from '../../../__generated__/graphql/catalog'
import { reportClientError } from '../../../app/_components/chrome/scripts/DataDogRumScript'
import { derivePricingForVariantGroup } from '../../../app/_components/pricing/PricingRange.client'
import { useFetchNostoPlacementData } from '../../../app/_hooks/useFetchNostoPlacementData'
import { NostoPlacementId } from '../../../types/nosto'
import { QueryStatus } from '../types'

const maxProductCountLimit = 12

export function useNostoRecos({
  nostoPlacementId,
  productCountLimit = maxProductCountLimit,
  skip,
}: {
  nostoPlacementId: NostoPlacementId
  productCountLimit?: number
  skip?: boolean
}) {
  /*
    We purposefully do not fetch much on the variant level. Nosto recommends Shopify products, not variants.
    So we want to focus on the variantGroup related to that variant and its data.
   */
  const [fetchRecsData, { data, error, called, loading }] = useLazyQuery(
    gql(`
      query GetDataForNostoRecs($skusForVariantGroups: [String!]) {
        productCatalogVariants(skus: $skusForVariantGroups) {
          id
          shopifyProductId
          fit {
            id
            displayValue
          }
          group {
            id
            title
            urlPath
            exclusive
            noSearchIndex
            availability
            color {
              id
              displayValue
            }
            priceRange {
              min {
                amount
              }
              max {
                amount
              }
              currencyCode
            }
            images(first: 1) {
              url
              altText
            }
            product {
              id
              handle
              options {
                colorsTotalCount
              }
            }
            defaultShopifyProduct {
              id
            }
          }
        }
      }
    `)
  )

  useUpdateEffect(() => {
    if (!error) return
    reportClientError({
      error,
      context: {},
    })
  }, [error])

  const {
    data: { skusForVariantGroups, title: placementTitle, resultId: placementResultId },
    status: nostoStatus,
  } = useFetchNostoPlacementData(nostoPlacementId, {
    skip: skip ?? false,
  })

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,  react-you-might-not-need-an-effect/no-event-handler
    if (!skip && skusForVariantGroups && skusForVariantGroups.length > 0) {
      fetchRecsData({
        variables: {
          skusForVariantGroups: skusForVariantGroups.slice(
            0,
            Math.min(productCountLimit, maxProductCountLimit)
          ),
        },
      })
    }
  }, [fetchRecsData, productCountLimit, skip, skusForVariantGroups])

  const productCatalogVariants = data?.productCatalogVariants
  const validVariants = useMemo(() => {
    if (error) return []
    return (
      (productCatalogVariants ?? [])
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ?.filter(v => !v.group.noSearchIndex && v.group.availability === 'AVAILABLE')
        .map(v => {
          return {
            ...v,
            pricing: derivePricingForVariantGroup(v.group),
          }
        })
    )
  }, [error, productCatalogVariants])

  const status = useMemo<QueryStatus>(() => {
    if (skip) return 'resolved'

    // We are waiting on Nosto JS
    if (nostoStatus === 'pending') return 'pending'

    // Failed
    if (error || nostoStatus === 'rejected') return 'rejected'

    // Notso came back with nothing for us
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!skusForVariantGroups || skusForVariantGroups.length === 0) return 'resolved'

    // Waiting on product details query
    if (!called || loading) return 'pending'

    return 'resolved'
  }, [called, error, loading, nostoStatus, skip, skusForVariantGroups])

  return {
    error,
    placementTitle,
    variants: validVariants,
    resultId: placementResultId,
    status,
  }
}
